import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { QRCodeCanvas } from 'qrcode.react';
import styled from '@emotion/styled';
import { Container, Form, Input, Button, Header, Segment, Grid, Label } from 'semantic-ui-react';

const QRCodeGenContainer = styled.div`
  padding: 20px 10px;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  @media (min-width: 768px) {
    padding: 40px 20px;
  }

  .qr-card {
    max-width: 900px;
    margin: 0 auto;
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    padding: 15px !important;

    @media (min-width: 768px) {
      padding: 2em 1em !important;
    }
  }

  .page-title {
    font-size: 1.5rem !important;
    margin-bottom: 20px !important;

    @media (min-width: 768px) {
      font-size: 2rem !important;
      margin-bottom: 30px !important;
    }
  }

  .qr-preview {
    background: white;
    padding: 20px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;

    @media (min-width: 768px) {
      padding: 30px;
      min-height: 400px;
    }
  }

  .qr-canvas-wrapper {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    max-width: 100%;
    overflow: hidden;

    @media (min-width: 768px) {
      padding: 20px;
    }

    canvas {
      max-width: 100% !important;
      height: auto !important;
      display: block;
    }
  }

  .controls {
    margin-top: 20px;
  }

  .color-picker-group {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (min-width: 768px) {
      gap: 15px;
    }
  }

  .color-input {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
    min-width: 120px;

    .ui.label {
      font-size: 0.9rem;
      white-space: nowrap;

      @media (min-width: 768px) {
        font-size: 1rem;
      }
    }
  }

  input[type='color'] {
    width: 100%;
    min-width: 50px;
    height: 45px;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    @media (min-width: 768px) {
      width: 60px;
      height: 40px;
    }
  }

  input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 5px;
    background: #d3d3d3;
    outline: none;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      background: #667eea;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 25px;
      height: 25px;
      border-radius: 50%;
      background: #667eea;
      cursor: pointer;
      border: none;
    }
  }

  .download-btn {
    margin-top: 20px !important;
    width: 100%;

    @media (min-width: 768px) {
      width: auto;
    }
  }

  .url-display {
    word-break: break-all;
    margin-top: 10px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 5px;
    font-size: 0.8em;
    color: #495057;
    max-width: 100%;
    text-align: center;

    @media (min-width: 768px) {
      font-size: 0.9em;
    }
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;

    input[type='checkbox'] {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    label {
      cursor: pointer;
      user-select: none;
      font-size: 1rem;
    }
  }

  .empty-state {
    text-align: center;
    color: #999;
    padding: 20px;

    h3 {
      font-size: 1.2rem !important;

      @media (min-width: 768px) {
        font-size: 1.5rem !important;
      }
    }

    p {
      font-size: 0.9rem;

      @media (min-width: 768px) {
        font-size: 1rem;
      }
    }
  }

  /* Input field styling */
  .ui.input input {
    font-size: 1rem !important;
    color: #000 !important;
    background-color: #fff !important;

    @media (max-width: 767px) {
      font-size: 0.95rem !important;
    }

    &::placeholder {
      color: #999 !important;
      opacity: 0.7;
    }
  }

  .ui.form label {
    font-weight: 600;
    font-size: 1rem;
    color: #333;
    margin-bottom: 8px;
  }

  .mobile-generate-btn {
    @media (min-width: 768px) {
      display: none !important;
    }
  }
`;

const QRCodeGenerator = () => {
  const location = useLocation();
  const [url, setUrl] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [qrSize, setQrSize] = useState(256);
  const [displaySize, setDisplaySize] = useState(256);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [fgColor, setFgColor] = useState('#000000');
  const [includeMargin, setIncludeMargin] = useState(true);
  const qrRef = useRef(null);

  // Calculate responsive QR size
  useEffect(() => {
    const calculateDisplaySize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        // On mobile, limit QR size to screen width minus padding
        const maxMobileSize = Math.min(qrSize, screenWidth - 100);
        setDisplaySize(maxMobileSize);
      } else {
        setDisplaySize(qrSize);
      }
    };

    calculateDisplaySize();
    window.addEventListener('resize', calculateDisplaySize);
    return () => window.removeEventListener('resize', calculateDisplaySize);
  }, [qrSize]);

  // Parse query string and auto-generate QR code if URL is provided
  useEffect(() => {
    const params = queryString.parse(location.search);
    if (params.url) {
      const decodedUrl = decodeURIComponent(params.url);
      setUrl(decodedUrl);
      setQrValue(decodedUrl);
    }
    // Optional: Parse other parameters like size, colors
    if (params.size) {
      const size = Number(params.size);
      if (size >= 128 && size <= 512) {
        setQrSize(size);
      }
    }
    if (params.bgColor) {
      setBgColor(`#${params.bgColor.replace('#', '')}`);
    }
    if (params.fgColor) {
      setFgColor(`#${params.fgColor.replace('#', '')}`);
    }
  }, [location.search]);

  const handleGenerate = (e) => {
    e.preventDefault();
    if (url.trim()) {
      setQrValue(url);
    }
  };

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'qrcode.png';
      link.href = url;
      link.click();
    }
  };

  const handleClear = () => {
    setUrl('');
    setQrValue('');
  };

  return (
    <QRCodeGenContainer>
      <Container fluid>
        <Segment className="qr-card">
          <Header as="h1" textAlign="center" className="page-title" style={{ color: '#667eea' }}>
            Generador de Códigos QR
          </Header>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column mobile={16} tablet={8} computer={8}>
                <Form onSubmit={handleGenerate}>
                  <Form.Field>
                    <label>URL o Texto</label>
                    <Input
                      placeholder="Ingresa la URL o texto..."
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      fluid
                    />
                    <Button
                      color="blue"
                      onClick={handleGenerate}
                      disabled={!url.trim()}
                      fluid
                      style={{ marginTop: '10px' }}
                    >
                      Generar
                    </Button>
                  </Form.Field>

                  <div className="controls">
                    <Form.Field>
                      <label>Tamaño del QR: {qrSize}px</label>
                      <input
                        type="range"
                        min="128"
                        max="512"
                        step="32"
                        value={qrSize}
                        onChange={(e) => setQrSize(Number(e.target.value))}
                      />
                    </Form.Field>

                    <div className="color-picker-group">
                      <div className="color-input">
                        <Label>Color del QR</Label>
                        <input
                          type="color"
                          value={fgColor}
                          onChange={(e) => setFgColor(e.target.value)}
                        />
                      </div>
                      <div className="color-input">
                        <Label>Color de Fondo</Label>
                        <input
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                        />
                      </div>
                    </div>

                    <Form.Field>
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          id="margin-checkbox"
                          checked={includeMargin}
                          onChange={(e) => setIncludeMargin(e.target.checked)}
                        />
                        <label htmlFor="margin-checkbox">Incluir margen</label>
                      </div>
                    </Form.Field>

                    {qrValue && (
                      <Button color="red" onClick={handleClear} fluid>
                        Limpiar
                      </Button>
                    )}
                  </div>
                </Form>
              </Grid.Column>

              <Grid.Column mobile={16} tablet={8} computer={8}>
                <div className="qr-preview">
                  {qrValue ? (
                    <>
                      <div ref={qrRef} className="qr-canvas-wrapper">
                        <QRCodeCanvas
                          value={qrValue}
                          size={displaySize}
                          bgColor={bgColor}
                          fgColor={fgColor}
                          level="H"
                          includeMargin={includeMargin}
                        />
                      </div>
                      <div className="url-display">
                        <strong>Contenido:</strong> {qrValue}
                      </div>
                      <Button
                        color="green"
                        onClick={handleDownload}
                        className="download-btn"
                        icon="download"
                        content="Descargar QR"
                        size="large"
                      />
                    </>
                  ) : (
                    <div className="empty-state">
                      <Header as="h3" style={{ color: '#999' }}>
                        El código QR aparecerá aquí
                      </Header>
                      <p>Ingresa una URL o texto y haz clic en "Generar"</p>
                    </div>
                  )}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </Container>
    </QRCodeGenContainer>
  );
};

export default QRCodeGenerator;
